/*
Eadin governance metadata for Demand Planning views.
This module centralizes quick summaries and downloadable governance text so each
view can explain its purpose, source-of-truth metric, and operating rules.
*/

const VIEW_GOVERNANCE = {
  "allocation-core": {
    viewId: "allocation-core",
    title: "SKU Allocation Governance",
    summary: "Use this view to manage governed allocation percentages at the SKU level inside each product family and subcategory.",
    purpose: "This is the operational allocation workspace for setting, reviewing, and validating final allocation percentages.",
    sourceOfTruth: "Adjusted Final % is the governed output once overrides, eligibility checks, and balancing rules are applied.",
    notFor: "Do not use this view as a purchasing plan, a forecast replacement, or a cash-planning surface.",
    keyRules: [
      "Percent changes must preserve governed balancing inside the scoped category and subcategory.",
      "Eligibility, override, and validation checks must be satisfied before an allocation is treated as operationally usable.",
      "Manual overrides are governance inputs, not proof that downstream purchasing should occur.",
    ],
    downstreamImpact: [
      "Allocation outputs influence downstream forecast interpretation and rebalance review.",
      "Incorrect allocation changes can distort later planning views even if table totals still appear balanced.",
    ],
    fullGuidance: [
      "Use this view when the planner needs to confirm how demand share is distributed across SKUs within the approved planning structure.",
      "The key governed output is Adjusted Final %, because it reflects the post-rule, post-override allocation state rather than raw user entry.",
      "This view should be treated as an allocation-governance surface, not as a substitute for purchase or cash planning.",
    ],
  },
  "demand-forecast": {
    viewId: "demand-forecast",
    title: "Forecast Demand Governance",
    summary: "Use this view to review forecast assumptions and the planning context that feeds downstream allocation and rebalance decisions.",
    purpose: "This is the forecast review surface for understanding demand expectations before downstream actions are approved.",
    sourceOfTruth: "The governed source of truth is the approved forecast context and its associated validation checks, not raw intuition or one-off edits.",
    notFor: "Do not use this view alone to issue purchase decisions or to bypass allocation governance.",
    keyRules: [
      "Forecast interpretation should precede rebalance decisions whenever material variance exists.",
      "This view provides planning context; it does not by itself authorize execution actions.",
      "Any manual forecast handling should remain explainable and aligned to workbook-backed planning logic.",
    ],
    downstreamImpact: [
      "Forecast interpretation drives later rebalance review and buy-planning confidence.",
      "Weak forecast governance can create false urgency in Open To Buy and related procurement views.",
    ],
    fullGuidance: [
      "Use this view to validate whether the current demand assumption remains credible before acting on downstream recommendations.",
      "The planner should treat it as a forecast-governance workspace and not as a direct execution queue.",
      "When variance signals are material, this view should inform whether later rebalance actions should be supported, watched, trimmed, or held.",
    ],
  },
  "eligibility-validation": {
    viewId: "eligibility-validation",
    title: "Eligibility & Validation Governance",
    summary: "Use this view to confirm whether each SKU is eligible and clean enough to participate in governed planning outputs.",
    purpose: "This is the validation control surface for identifying rule failures, missing fields, or conditions that block a SKU from governed use.",
    sourceOfTruth: "Eligibility and validation check columns are the source of truth for whether a row should be trusted downstream.",
    notFor: "Do not treat this view as a performance ranking or demand recommendation surface.",
    keyRules: [
      "A SKU that fails validation should not be treated as fully trustworthy in downstream planning views.",
      "Validation failures should be resolved at the data and governance level, not hidden by manual interpretation.",
      "Passing rows remain subject to the specific governance of each downstream view.",
    ],
    downstreamImpact: [
      "Validation failures can distort allocation, forecast interpretation, and procurement calculations.",
      "This view is the first checkpoint for data trust before planning outputs are operationalized.",
    ],
    fullGuidance: [
      "Use this view whenever the planner needs to understand whether a row is eligible to participate in governed planning decisions.",
      "It is especially important before acting on allocation, rebalance, or buy outputs.",
      "Treat this as a trust filter rather than a decision engine.",
    ],
  },
  "monthly-trend": {
    viewId: "monthly-trend",
    title: "Monthly Trend Governance",
    summary: "Use this view to compare current-month forecast and actual pacing at a practical operating level.",
    purpose: "This is the short-horizon trend surface for reading near-term movement and current-month pacing.",
    sourceOfTruth: "Month-level forecast, actual, and variance fields are the source of truth for short-term trend interpretation.",
    notFor: "Do not use this view as the only basis for structural allocation changes or long-range purchasing commitments.",
    keyRules: [
      "Monthly variance should be interpreted as a signal, not an automatic action order.",
      "Short-horizon trend changes should be reviewed alongside forecast and governance context.",
      "This view is directional and diagnostic rather than final for execution decisions.",
    ],
    downstreamImpact: [
      "Monthly pacing can raise or lower confidence in forecast and rebalance discussions.",
      "It should inform review cadence rather than replace governed decision logic.",
    ],
    fullGuidance: [
      "Use Monthly Trend to understand what is happening now, especially when current month performance is diverging from expectation.",
      "This view is best treated as a diagnostic lens that supports downstream governance conversations.",
      "It should not override more structured approval logic in other planning views.",
    ],
  },
  historical: {
    viewId: "historical",
    title: "Historical Governance",
    summary: "Use this view to understand trailing performance patterns and historical baseline behavior.",
    purpose: "This is the backward-looking reference view for prior-period revenue and planning context.",
    sourceOfTruth: "Historical revenue and recalculated totals are the source of truth for prior-period performance reading.",
    notFor: "Do not use historical values alone as a direct instruction for current buying or rebalancing.",
    keyRules: [
      "Historical performance is a reference baseline, not an automatic mandate for current actions.",
      "Comparisons should be made alongside current forecast and actual context.",
      "Use historical context to explain why a signal matters, not to skip current governance checks.",
    ],
    downstreamImpact: [
      "Historical context supports more credible forecast, rebalance, and OTB interpretation.",
      "Misreading history as a hard rule can overstate demand confidence.",
    ],
    fullGuidance: [
      "Use Historical when you need to ground current planning in what the business actually did before.",
      "This view helps with pattern recognition, but it should not be treated as the sole authority for present decisions.",
      "Historical context is strongest when paired with current variance and execution context.",
    ],
  },
  "forecast-vs-actuals": {
    viewId: "forecast-vs-actuals",
    title: "Forecast Vs Actuals Governance",
    summary: "Use this view to measure whether the current plan is pacing above or below real-world actuals.",
    purpose: "This is the variance-control surface for comparing forecast expectations against actual performance.",
    sourceOfTruth: "Forecast, actual, and variance outputs are the source of truth for identifying pacing gaps.",
    notFor: "Do not use this view by itself to issue buy quantities or to bypass later governance review.",
    keyRules: [
      "Variance is a trigger for review, not automatically a trigger for execution.",
      "Material underperformance or outperformance should be interpreted through governed thresholds.",
      "This view informs rebalance and forecast discussion before operational actions are taken.",
    ],
    downstreamImpact: [
      "Variance interpretation feeds Demand Rebalance and broader forecast governance.",
      "Poor variance handling can lead to unnecessary or premature execution changes.",
    ],
    fullGuidance: [
      "Use Forecast Vs Actuals when you need a disciplined answer to whether the business is pacing to plan.",
      "The important output is the direction and magnitude of the variance, not just the existence of a gap.",
      "Any downstream action should still be filtered through the specific governance of the actioning view.",
    ],
  },
  "forecast-by-sku": {
    viewId: "forecast-by-sku",
    title: "Forecast By SKU Governance",
    summary: "Use this view to inspect how forecast demand is distributed at the SKU level within the current plan.",
    purpose: "This is the SKU-granular forecast surface for reviewing unit and revenue expectations by item.",
    sourceOfTruth: "SKU-level forecast units and revenue fields are the source of truth for item-level forecast interpretation.",
    notFor: "Do not use this view as a substitute for final purchasing governance or procurement rounding logic.",
    keyRules: [
      "Item-level forecast should be reviewed in the context of allocation and subcategory governance.",
      "Forecast visibility does not equal execution approval.",
      "Use SKU detail to investigate a signal, not to skip staged governance.",
    ],
    downstreamImpact: [
      "Forecast By SKU helps explain later OTB and rebalance behavior at a row level.",
      "This view improves transparency when users need to understand what is driving a downstream recommendation.",
    ],
    fullGuidance: [
      "Use Forecast By SKU when a planner needs line-level detail instead of category or aggregate summaries.",
      "This view is especially useful for validating whether a downstream recommendation is directionally credible.",
      "It is diagnostic and explanatory, not a direct purchasing instruction surface.",
    ],
  },
  "open-to-buy": {
    viewId: "open-to-buy",
    title: "Open To Buy Governance",
    summary: "Use this view to review workbook-backed replenishment need and governed units to order before approval.",
    purpose: "This is the main replenishment review queue for workbook-backed OTB planning rows.",
    sourceOfTruth: "Units to Order and workbook-backed OTB planning fields are the source of truth in this view.",
    notFor: "Do not confuse this view with approved execution, cash liability settlement, or procurement rounding logic.",
    keyRules: [
      "This view is for reviewing buy need before final execution approval.",
      "Approved status should be set deliberately and should remain auditable.",
      "Workbook-backed row logic should remain the governing basis for OTB quantities.",
    ],
    downstreamImpact: [
      "Approved rows feed Approved Buys and later supplier execution workflows.",
      "Misinterpreting this view can lead to premature commitment before procurement governance is complete.",
    ],
    fullGuidance: [
      "Use Open To Buy to decide whether a replenishment need is valid enough to move toward approval.",
      "The critical field is Units to Order, because it reflects the workbook-backed replenishment calculation rather than a planner impression.",
      "This view is still pre-execution; later views govern what happens once an item is approved or transformed for procurement.",
    ],
  },
  "approved-buys": {
    viewId: "approved-buys",
    title: "Approved Buys Governance",
    summary: "Use this view to review rows that have already been approved for the next step of buy handling.",
    purpose: "This is the post-approval staging surface for grouped review of approved replenishment items.",
    sourceOfTruth: "Approved status and the approved workbook-backed buy rows are the source of truth here.",
    notFor: "Do not use this view to reinterpret whether a row should have needed replenishment in the first place.",
    keyRules: [
      "Only approved rows belong here.",
      "This view should preserve auditability between approval and downstream execution review.",
      "Grouping or staging should not overwrite the underlying approved record state.",
    ],
    downstreamImpact: [
      "Approved Buys bridges OTB approval and supplier-facing planning workflows.",
      "Errors here can break trust in the approval audit trail.",
    ],
    fullGuidance: [
      "Use Approved Buys as the controlled handoff between replenishment approval and downstream execution planning.",
      "It should remain traceable to the exact approval decision made upstream.",
      "This is not the place to re-litigate forecast or allocation assumptions.",
    ],
  },
  "overstock-inventory": {
    viewId: "overstock-inventory",
    title: "Overstock Governance",
    summary: "Use this view to identify excess inventory exposure and prioritize review by overstock severity.",
    purpose: "This is the inventory-risk surface for understanding excess units and excess value by SKU.",
    sourceOfTruth: "Overstock level, excess units, and excess value are the source of truth for overstock interpretation.",
    notFor: "Do not use this view as a demand-generation signal or a replacement for buy governance.",
    keyRules: [
      "Overstock signals should trigger review, containment, or planning adjustment rather than automatic action.",
      "Severity filtering should preserve transparency into why a row is considered excess.",
      "This view is risk-oriented, not purchase-oriented.",
    ],
    downstreamImpact: [
      "Overstock governance informs how aggressively planners should support new buying or rebalancing.",
      "Excess exposure should temper downstream replenishment confidence.",
    ],
    fullGuidance: [
      "Use the Overstock view to understand where inventory risk is accumulating.",
      "It is best used as a constraint and caution signal for later planning decisions.",
      "Do not treat it as a direct instruction engine on its own.",
    ],
  },
  "ai-rebalance": {
    viewId: "ai-rebalance",
    title: "Demand Rebalance Governance",
    summary: "Use this view to review deterministic, governance-driven rebalance recommendations before approving changes.",
    purpose: "This is the governed recommendation workspace for staged top-line, category, and subcategory rebalance review.",
    sourceOfTruth: "The deterministic rebalance logic and its computed recommended percentages are the source of truth, not a prompt-driven model output.",
    notFor: "Do not treat this view as an AI-generated suggestion engine or an automatic execution surface.",
    keyRules: [
      "Numeric recommendations are deterministic and governed by rules-based thresholds.",
      "Rebalance proposals should be reviewed before approval and should remain explainable.",
      "The view is intended for governance and approval, not blind automation.",
    ],
    downstreamImpact: [
      "Approved rebalance decisions change how allocation outputs are understood and actioned downstream.",
      "Confusing this view for an AI black box would weaken trust and explainability.",
    ],
    fullGuidance: [
      "Use Demand Rebalance when forecast variance and governed logic suggest that allocation share should be reviewed.",
      "The source of truth is the deterministic recommendation engine already built into the product, including the computed before, after, and delta outputs.",
      "This view exists to make governed recommendations explainable and reviewable before they are approved.",
    ],
  },
  "demand-distribution-profile": {
    viewId: "demand-distribution-profile",
    title: "Demand Distribution Profile Governance",
    summary: "Use this view to understand how demand is distributed across products, categories, and subcategories over time.",
    purpose: "This is a profile and mix-reading surface for understanding structural demand distribution.",
    sourceOfTruth: "Distribution percentages and supporting demand context are the source of truth for mix interpretation.",
    notFor: "Do not use this view as a direct buy, approval, or procurement instruction surface.",
    keyRules: [
      "This view explains distribution structure rather than executing decisions.",
      "Use it to understand mix shape and concentration before acting elsewhere.",
      "Profile interpretation should remain tied to workbook-backed planning data.",
    ],
    downstreamImpact: [
      "This view supports better rebalance and forecast interpretation.",
      "It reduces ambiguity when planners need to see how demand is spread across the catalog.",
    ],
    fullGuidance: [
      "Use Demand Distribution Profile as a structural lens on the business rather than a workflow queue.",
      "It is especially helpful for explaining why later recommendations are concentrated in certain products or categories.",
      "The goal is interpretation, not execution.",
    ],
  },
  "cash-liabilities": {
    viewId: "cash-liabilities",
    title: "Cash Liabilities Governance",
    summary: "Use this view to understand the financial liability associated with planned or approved buy exposure.",
    purpose: "This is the finance-facing view for reading exposure tied to demand-planning buy quantities and timing.",
    sourceOfTruth: "Liability value, unit cost, and timing fields are the source of truth for cash-exposure interpretation.",
    notFor: "Do not use this view to create or round procurement quantities.",
    keyRules: [
      "This view is about financial exposure, not replenishment validity.",
      "Cash interpretation should remain linked to the underlying governed quantities and dates.",
      "Finance visibility should not overwrite procurement governance.",
    ],
    downstreamImpact: [
      "Cash Liabilities helps finance and planning coordinate on timing and risk.",
      "It gives visibility into whether downstream commitments are operationally and financially tolerable.",
    ],
    fullGuidance: [
      "Use Cash Liabilities to understand the value and timing of planning commitments that are building inside the system.",
      "It translates planning quantities into financial exposure.",
      "The view is interpretive and financial, not the source of procurement quantity logic.",
    ],
  },
  "upcoming-buys": {
    viewId: "upcoming-buys",
    title: "Upcoming Buys Governance",
    summary: "Use this view as a month-switched staging surface where one canonical Upcoming Buys model is fed by the selected month source rather than by month-specific UI logic.",
    purpose: "This is the governed buy-planning surface for reviewing supplier-ready upcoming purchasing rows inside one shared operational structure.",
    sourceOfTruth: "The source of truth is the selected month dataset after it is normalized into the shared Upcoming Buys row model used by the table, exports, notes, and actions.",
    notFor: "Do not hardcode June, March, or December behavior directly into the UI, and do not use this view to invent new quantity logic outside the governed source mapping.",
    keyRules: [
      "The month selector should only change the active source month, such as March, June, or December.",
      "Each month must map into the same canonical Upcoming Buys row structure before it reaches the UI.",
      "Date formatting, currency formatting, blank notes behavior, supplier grouping, review states, and export rules must stay consistent across all months.",
    ],
    downstreamImpact: [
      "This governance keeps June as the first configured month without turning it into a special-case table implementation.",
      "It allows March and December to plug into the same UI, export, and supplier-action flow by adding source mappings instead of rebuilding the view.",
    ],
    fullGuidance: [
      "Upcoming Buys should operate from a single canonical data model. The table, filters, exports, notes, approval actions, and formatting rules should not change by month. The selected month should only determine which source dataset is loaded into the normalized Upcoming Buys structure.",
      "When a user selects a month such as June, the system should read the June source tab or workbook mapping, transform that source into the shared UpcomingBuyRow shape, and then render the standard Upcoming Buys experience from that normalized dataset. The UI should never contain month-specific logic beyond selecting the active source.",
      "For June, the implementation standard is June source tab to month adapter to normalized Upcoming Buys rows to shared UI and export logic. If a future request introduces a difference, the first question should be whether it belongs in the month adapter or whether it is a true cross-month business-rule change that needs separate governance approval.",
    ],
  },
  "po-schedule": {
    viewId: "po-schedule",
    title: "PO Schedule Governance",
    summary: "Use this view to translate governed planning rows into supplier-facing PO timing structure.",
    purpose: "This is the supplier scheduling surface for assigning or reviewing PO dates, cadence, and grouped supplier activity.",
    sourceOfTruth: "Scheduled dates, supplier grouping, and governed planning quantities are the source of truth in this view.",
    notFor: "Do not use this view to decide whether demand exists or to create fresh quantity logic.",
    keyRules: [
      "The PO Schedule should inherit governed quantities from upstream planning views.",
      "Date and cadence handling should remain supplier- and workflow-aware.",
      "This is a scheduling and coordination surface, not a demand-creation surface.",
    ],
    downstreamImpact: [
      "PO Schedule is the operational bridge from planning into supplier-facing action timing.",
      "Errors here can cause timing confusion even when the underlying quantity logic is correct.",
    ],
    fullGuidance: [
      "Use PO Schedule when governed planning outputs are ready to be organized into supplier timing.",
      "The critical point is that scheduling sits downstream of quantity truth; it should not redefine what is being bought.",
      "Treat it as an execution-timing view rather than a planning-formula view.",
    ],
  },
  "bundle-demand-explosion": {
    viewId: "bundle-demand-explosion",
    title: "Bundle Demand Explosion Governance",
    summary: "Use this view to trace bundle demand into the component-level demand obligations that it creates downstream.",
    purpose: "This is the lineage view for understanding how bundle demand expands into child component demand.",
    sourceOfTruth: "Workbook-aligned bundle-to-component transformation logic is the source of truth here.",
    notFor: "Do not use this view to invent new standalone demand beyond what the explosion logic produces.",
    keyRules: [
      "Bundle demand must explode according to governed component relationships.",
      "The view exists to explain lineage, not to collapse bundle and component logic into one number.",
      "Auditability matters because downstream supply planning depends on correct component attribution.",
    ],
    downstreamImpact: [
      "This view feeds trust in downstream component demand and procurement interpretation.",
      "If the explosion logic is misunderstood, later supply decisions will be mis-scoped.",
    ],
    fullGuidance: [
      "Use Bundle Demand Explosion to answer where component demand came from when bundle-level activity exists.",
      "It should make the lineage explicit and auditable.",
      "The purpose is explanation and reconciliation, not independent quantity invention.",
    ],
  },
  "replacement-demand-shift": {
    viewId: "replacement-demand-shift",
    title: "Replacement Demand Shift Governance",
    summary: "Use this view to trace how demand should shift from sunset products into their governed replacements.",
    purpose: "This is the replacement-lineage surface for direct sunset-to-replacement demand movement.",
    sourceOfTruth: "Workbook-backed replacement mapping and governed shift logic are the source of truth in this view.",
    notFor: "Do not use this view to create arbitrary substitution paths outside the governed mapping.",
    keyRules: [
      "Replacement movement must follow the governed source-to-destination relationship.",
      "This view should preserve explainability of why demand moved.",
      "Only mapped and governed shifts should be treated as valid downstream demand transfer.",
    ],
    downstreamImpact: [
      "This view protects later planning from treating sunset loss as pure disappearance when a replacement exists.",
      "It improves trust in downstream allocation and procurement interpretation.",
    ],
    fullGuidance: [
      "Use Replacement Demand Shift when a planner needs to understand how demand leaves one product and lands in another.",
      "The logic should remain tightly governed so the transfer is explainable and auditable.",
      "This is a lineage and interpretation view, not a freeform reallocation tool.",
    ],
  },
  "bundle-replacement-shift": {
    viewId: "bundle-replacement-shift",
    title: "Bundle Replacement Shift Governance",
    summary: "Use this view to understand replacement-demand movement when bundles and their components are both part of the shift logic.",
    purpose: "This is the combined lineage surface for replacement behavior that involves bundle structures.",
    sourceOfTruth: "Governed bundle and replacement transformation rules are the source of truth for this view.",
    notFor: "Do not use this view to flatten bundle and replacement logic into unsupported manual assumptions.",
    keyRules: [
      "Replacement relationships must stay consistent with the governed bundle structure.",
      "Component lineage should remain traceable after the shift occurs.",
      "The view is for validation and explanation of complex transformation logic.",
    ],
    downstreamImpact: [
      "This view helps prevent broken lineage when multiple transformation rules interact.",
      "It preserves confidence in later component-level and procurement-facing planning outputs.",
    ],
    fullGuidance: [
      "Use Bundle Replacement Shift when the planner needs to verify complex replacement behavior involving bundled products.",
      "This view should make the combined logic understandable rather than opaque.",
      "It is especially useful as an audit surface before later supply interpretation.",
    ],
  },
  "component-demand-ledger": {
    viewId: "component-demand-ledger",
    title: "Component Demand Ledger Governance",
    summary: "Use this view to reconcile final demand at the one-row-per-supply-SKU component level.",
    purpose: "This is the final reconciliation view for component demand after upstream transformations are applied.",
    sourceOfTruth: "The ledger’s final one-row-per-supply-SKU demand output is the source of truth in this view.",
    notFor: "Do not use this view to reverse-engineer unsupported transformation rules or overwrite earlier lineage logic.",
    keyRules: [
      "This is the downstream reconciliation output after bundle and replacement logic has been applied.",
      "Each supply SKU should land in a clean, auditable final component-demand position.",
      "The value of this view is trust in final rolled-up component demand.",
    ],
    downstreamImpact: [
      "This ledger becomes a key reference for later procurement interpretation and material planning.",
      "If it is unclear or broken, confidence in all upstream transformations falls.",
    ],
    fullGuidance: [
      "Use Component Demand Ledger as the downstream truth table for final component demand.",
      "It should answer what the system believes each supply SKU ultimately needs after all governed transformations are considered.",
      "This is a reconciliation surface, not a place to improvise new planning logic.",
    ],
  },
  "purchase-rounding": {
    viewId: "purchase-rounding",
    title: "Purchase Rounding Governance",
    summary: "Use this view to see how approved demand becomes governed purchasable quantities through MOQ, carton, and pooled-container logic.",
    purpose: "This is the procurement-governance view for transforming valid demand into operationally purchasable units.",
    sourceOfTruth: "Adjusted Rounded Units is the key downstream source of truth for purchasable quantity.",
    notFor: "Do not treat this view as new demand creation; it only applies procurement constraints to existing need.",
    keyRules: [
      "MOQ and carton rounding are procurement-validity controls, not demand signals.",
      "Container pooling may add governed fill units to improve execution efficiency.",
      "Adjusted Rounded Units should be used downstream instead of raw OTB units where procurement validity matters.",
    ],
    downstreamImpact: [
      "Purchase Rounding feeds Inventory Capital Plan and Container Optimization.",
      "Misreading it as demand generation would distort procurement and finance trust.",
    ],
    fullGuidance: [
      "Use Purchase Rounding when the planner needs to understand why buy quantities differ from initial demand quantities.",
      "The important number is Adjusted Rounded Units because it reflects the final procurement-valid quantity after MOQ, carton, and container logic.",
      "This view is governance for operational buying, not a signal that demand itself increased.",
    ],
  },
  "inventory-capital-plan": {
    viewId: "inventory-capital-plan",
    title: "Inventory Capital Plan Governance",
    summary: "Use this view to translate governed rounded purchase quantities into timing and cash exposure by month and category.",
    purpose: "This is the finance-planning surface for reading capital requirements created by governed procurement quantities.",
    sourceOfTruth: "Cash Required derived from Adjusted Rounded Units and Unit Cost is the source of truth in this view.",
    notFor: "Do not use this view to invent procurement quantities or override purchase-rounding governance.",
    keyRules: [
      "Finance should read this view from governed rounded quantities rather than theoretical demand.",
      "Unit cost completeness matters because missing cost weakens downstream capital confidence.",
      "This view is about exposure and timing, not quantity origination.",
    ],
    downstreamImpact: [
      "This view informs working-capital planning and purchase timing expectations.",
      "It helps finance understand the consequence of governed procurement decisions.",
    ],
    fullGuidance: [
      "Use Inventory Capital Plan to answer how much capital the governed procurement plan is likely to consume and when.",
      "The view should remain downstream of Purchase Rounding and should never become the place where quantity logic is invented.",
      "Its purpose is financial clarity, not procurement recalculation.",
    ],
  },
  "container-optimization": {
    viewId: "container-optimization",
    title: "Container Optimization Governance",
    summary: "Use this view to explain pooled container fill decisions that improve freight and supplier execution without creating new demand.",
    purpose: "This is the pooled-procurement optimization surface for container efficiency review.",
    sourceOfTruth: "Group base buy, fill units, and adjusted final grouped quantities are the source of truth for pooled container planning.",
    notFor: "Do not treat fill units as market demand; they are procurement-optimization inventory.",
    keyRules: [
      "Container fill exists to improve procurement efficiency and supplier execution quality.",
      "Fill units are not new demand and should not be explained that way downstream.",
      "Grouped supplier pools must remain traceable and auditable.",
    ],
    downstreamImpact: [
      "Container Optimization clarifies why grouped procurement may overbuy relative to base demand.",
      "It protects trust in freight and supplier planning by making optimization explicit.",
    ],
    fullGuidance: [
      "Use Container Optimization to understand when the system intentionally adds governed fill quantity so a supplier pool can ship more efficiently.",
      "The view exists to make pooled procurement explainable rather than mysterious.",
      "The central rule is that fill inventory is optimization-driven inventory, not newly discovered customer demand.",
    ],
  },
  "cogs-canonical-view": {
    viewId: "cogs-canonical-view",
    title: "COGS Canonical View Governance",
    summary: "Use this view to review the finance-owned canonical unit cost that supports reporting, close, and governed COGS interpretation by Supply SKU.",
    purpose: "This is the canonical finance cost surface for understanding what one unit of a SKU truly costs the business for accounting and reporting purposes.",
    sourceOfTruth: "One row per Supply SKU is the governing grain. BOM cost is the structural baseline, landed inputs are finance-controlled, and Weighted Cost is the preferred accounting-aligned basis when available.",
    notFor: "Do not use this view as a procurement action queue, demand-planning surface, or upstream master-cost editing workflow.",
    keyRules: [
      "Finance owns the landed-input layer and the canonical cost interpretation used for close.",
      "Total BOM Cost remains structural product cost and should not be overwritten through canonical COGS handling.",
      "Weighted Cost may differ from Landed Cost because it reflects ERP inventory valuation rather than only current product physics.",
    ],
    downstreamImpact: [
      "This view supports finance close, P&L reporting, and trust in canonical COGS interpretation downstream.",
      "Incorrect landed inputs or missing weighted cost can distort reporting confidence even when the row still appears operationally complete.",
    ],
    fullGuidance: [
      "Use COGS Canonical View when finance or operations needs the governed source of truth for per-SKU unit cost used in reporting and close.",
      "Keep the layers conceptually separate: BOM cost explains product structure, landed cost explains finance-controlled adjustments, and weighted cost explains accounting reality in the ERP valuation layer.",
      "This view is intentionally audit-friendly and should be treated as a reference surface for canonical cost interpretation rather than an execution workspace.",
    ],
  },
};

/** Maps Demand Planning preset ids to governance registry keys where they differ. */
export const DEMAND_PLANNING_PRESET_TO_GOVERNANCE_ID = {
  "sku-allocation": "allocation-core",
  "forecast-demand": "demand-forecast",
};

export const resolveGovernanceViewId = (viewId) =>
  DEMAND_PLANNING_PRESET_TO_GOVERNANCE_ID[viewId] ?? viewId;

export const getViewGovernance = (viewId) => {
  const resolvedId = resolveGovernanceViewId(viewId);
  return VIEW_GOVERNANCE[resolvedId] ?? {
    viewId,
    title: "Demand Planning Governance",
    summary: "This view participates in the broader Demand Planning workflow and should be interpreted through governed, workbook-backed logic.",
    purpose: "This view is part of the Eadin Demand Planning system and should be used according to the controlled workflow for the active planning surface.",
    sourceOfTruth: "Use the active view’s governed output fields rather than ad hoc interpretation.",
    notFor: "Do not treat this view as an unsupported shortcut around the planning workflow.",
    keyRules: [
      "Follow the specific purpose of the active view.",
      "Preserve auditability and explainability in planning decisions.",
      "Treat governed outputs as the downstream source of truth.",
    ],
    downstreamImpact: [
      "Incorrect interpretation in one view can affect trust in downstream planning and procurement surfaces.",
    ],
    fullGuidance: [
      "This view is part of the governed Demand Planning workspace.",
      "Users should rely on the active view’s controlled outputs and not assume every view serves the same purpose.",
    ],
  };
};

export const buildGovernanceDownloadText = (governance) => {
  return [
    `${governance.title}`,
    "",
    `Quick Summary: ${governance.summary}`,
    `Purpose: ${governance.purpose}`,
    `Source of Truth: ${governance.sourceOfTruth}`,
    `Not For: ${governance.notFor}`,
    "",
    "Key Rules:",
    ...governance.keyRules.map(rule => `- ${rule}`),
    "",
    "Downstream Impact:",
    ...governance.downstreamImpact.map(item => `- ${item}`),
    "",
    "Full Guidance:",
    ...governance.fullGuidance.map(item => `- ${item}`),
    "",
    "Generated from the Eadin Demand Planning governance helper.",
  ].join("\n");
}

export const buildAllGovernanceDownloadText = () => {
  const orderedGovernance = Object.values(VIEW_GOVERNANCE).sort((left, right) =>
    left.title.localeCompare(right.title),
  );

  return [
    "Eadin Demand Planning Governance",
    "",
    "This document aggregates the governance guidance for the Demand Planning views currently available in the workspace.",
    "",
    ...orderedGovernance.flatMap(governance => [
      "=".repeat(72),
      buildGovernanceDownloadText(governance),
      "",
    ]),
    "Generated from the Eadin Demand Planning governance helper.",
  ].join("\n");
}
